import Button from '@mui/joy/Button';
import { useTranslation } from 'react-i18next';

import { type UseToggle } from '@/shared/hooks/UseToggle';

import { typedMemo } from '@/shared/utils/react';

import KpcModal from './layout/KpcModal';
import Row from './layout/Row';
import { SaveIcon } from '../../icons/Icons';

type Props<T> = React.PropsWithChildren<{
  bodyText?: string;
  hideOnConfirm?: boolean;
  loading?: boolean;
  onCancel?: () => void;
  onConfirm: () => void;
  toggle: UseToggle<T>;
}>;

const ConfirmModal = <T,>({
  bodyText,
  children,
  hideOnConfirm = true,
  loading = false,
  onConfirm,
  onCancel = () => {},
  toggle,
}: Props<T>) => {
  const { t } = useTranslation();

  const handleConfirm = () => {
    onConfirm();
    if (hideOnConfirm) {
      toggle.hide();
    }
  };

  const handleCancel = () => {
    onCancel();
    toggle.hide();
  };

  return (
    <KpcModal
      className="w-1/2 overflow-y-scroll"
      icon={<SaveIcon />}
      title={t('modals.confirm.title')}
      toggle={toggle}
    >
      <Row>{bodyText ?? t('modals.confirm.description')}</Row>
      {children}
      <Row className="justify-end">
        <Button color="neutral" disabled={loading} size="sm" variant="soft" onClick={handleCancel}>
          {t('buttons.cancel')}
        </Button>
        <Button color="success" loading={loading} size="sm" variant="solid" onClick={handleConfirm}>
          {t('buttons.confirm')}
        </Button>
      </Row>
    </KpcModal>
  );
};

export default typedMemo(ConfirmModal);
